import { useCallback } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { PageItemErrorBoundary } from '@/components/page/Items/PageItemErrorBoundary'

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 320px;
  padding: 24px;
  box-sizing: border-box;
  color: #333;
  font-size: 14px;
  text-align: center;
`

const Action = styled.button`
  border: 1px solid #333;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font: inherit;
  padding: 6px 14px;
  cursor: pointer;
`

/**
 * The net under the canvas as a whole: an error a page item's own net did
 * not hold (the page, the overlay, the rulers) gives way to a message and a
 * reload, with the header — Back, Save — and the menus still standing, so
 * the person is never left with a blank window.
 */
const CanvasGuard = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('Canvas')
  const onError = useCallback(
    (error: Error, info: ErrorInfo) => {
      handleError(t('editor.canvasFallback.toast'), error)
      console.error('the canvas could not be drawn', info.componentStack)
    },
    [handleError, t],
  )
  return (
    <PageItemErrorBoundary
      onError={onError}
      fallback={(_error, retry) => (
        <Fallback data-canvas-fallback>
          <span>{t('editor.canvasFallback.message')}</span>
          <Action type="button" onClick={retry}>
            {t('editor.canvasFallback.retry')}
          </Action>
        </Fallback>
      )}
    >
      {children}
    </PageItemErrorBoundary>
  )
}

export default CanvasGuard
